import React, { useState, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { apiClient } from '../api/client';
import UnlockClientModal from './UnlockClientModal';
import { isClientUnlocked, unlockClientWithPassphrase, getClientSessionKey } from '../crypto/sessionKeyStore';
import { base64ToBytes } from '../crypto/bytes';
import { encryptToEnvelope } from '../crypto/envelope';

function FileUpload({ selectedClient, onJobCreated }) {
  const [files, setFiles] = useState([]);
  const [uploading, setUploading] = useState(false);
  const [uploadProgress, setUploadProgress] = useState({});
  const [error, setError] = useState(null);
  const [dragOver, setDragOver] = useState(false);
  const [showUnlockModal, setShowUnlockModal] = useState(false);
  const [unlockError, setUnlockError] = useState('');
  const [unlocking, setUnlocking] = useState(false);
  const [unlockClientRecord, setUnlockClientRecord] = useState(null);
  const fileInputRef = useRef(null);
  const navigate = useNavigate();

  const handleDragOver = (e) => {
    e.preventDefault();
    setDragOver(true);
  };

  const handleDragLeave = (e) => {
    e.preventDefault();
    setDragOver(false);
  };

  const handleDrop = (e) => {
    e.preventDefault();
    setDragOver(false);
    const droppedFiles = Array.from(e.dataTransfer.files).filter(
      (f) => f.type === 'application/pdf'
    );
    addFiles(droppedFiles);
  };

  const handleFileSelect = (e) => {
    const selectedFiles = Array.from(e.target.files);
    addFiles(selectedFiles);
  };

  const addFiles = (newFiles) => {
    const pdfFiles = newFiles.filter((f) => f.type === 'application/pdf');
    setFiles((prev) => [...prev, ...pdfFiles]);
    setError(null);
  };

  const removeFile = (index) => {
    setFiles((prev) => prev.filter((_, i) => i !== index));
  };

  const uploadFiles = async () => {
    if (!selectedClient) {
      setError('Please select a client first');
      return;
    }

    if (files.length === 0) {
      setError('Please select at least one PDF file');
      return;
    }

    const unlocked = await ensureClientUnlocked();
    if (!unlocked) {
      return;
    }

    setUploading(true);
    setError(null);

    try {
      // Get presigned URLs for all files
      const fileInfos = files.map((f) => ({
        filename: f.name,
        content_type: 'application/pdf',
      }));

      const response = await apiClient.requestUpload(
        selectedClient.client_id,
        fileInfos
      );

      onJobCreated(response);

      const dekBytes = getClientSessionKey(selectedClient.client_id);
      if (!dekBytes) {
        throw new Error('Client is locked. Unlock and retry upload.');
      }

      // Upload each file to S3
      for (let i = 0; i < files.length; i++) {
        const file = files[i];
        const uploadInfo = response.uploads[i];

        setUploadProgress((prev) => ({
          ...prev,
          [file.name]: { status: 'uploading', progress: 0 },
        }));

        try {
          await uploadToS3(file, uploadInfo, dekBytes);
          setUploadProgress((prev) => ({
            ...prev,
            [file.name]: { status: 'complete', progress: 100 },
          }));
        } catch (err) {
          setUploadProgress((prev) => ({
            ...prev,
            [file.name]: { status: 'error', error: err.message },
          }));
        }
      }

      // Navigate to status page
      setTimeout(() => {
        navigate(`/status/${response.job_id}`);
      }, 1000);
    } catch (err) {
      setError('Failed to initiate upload: ' + err.message);
    } finally {
      setUploading(false);
    }
  };

  const ensureClientUnlocked = async () => {
    if (!selectedClient?.client_id) {
      setError('Please select a client first');
      return false;
    }

    if (isClientUnlocked(selectedClient.client_id)) {
      return true;
    }

    try {
      const fullClient = await apiClient.getClient(selectedClient.client_id);
      if (!fullClient?.key_bundle) {
        setError('Client key bundle is missing. Re-enroll client encryption settings.');
        return false;
      }
      setUnlockClientRecord(fullClient);
      setUnlockError('');
      setShowUnlockModal(true);
    } catch (err) {
      setError(`Failed to load client key metadata: ${err.message}`);
    }
    return false;
  };

  const handleUnlock = async (passphrase) => {
    if (!unlockClientRecord) {
      return;
    }

    try {
      setUnlocking(true);
      setUnlockError('');
      await unlockClientWithPassphrase(unlockClientRecord, passphrase);
      setShowUnlockModal(false);
      setUnlockClientRecord(null);
    } catch (err) {
      setUnlockError(err.message || 'Unlock failed.');
    } finally {
      setUnlocking(false);
    }
  };

  const uploadToS3 = async (file, uploadInfo, dekBytes) => {
    const plaintextBytes = new Uint8Array(await file.arrayBuffer());
    const aadBytes = base64ToBytes(uploadInfo.encryption?.aad_b64 || '');
    if (aadBytes.length === 0) {
      throw new Error('Missing encryption metadata for upload.');
    }

    const envelope = await encryptToEnvelope({
      keyBytes: dekBytes,
      plaintextBytes,
      aadBytes,
    });

    const encryptedManifest = {
      manifest_version: 'amdmd-file-manifest-v1',
      encryption: {
        version: uploadInfo.encryption.version,
        object_id: uploadInfo.encryption.object_id,
        aad_b64: uploadInfo.encryption.aad_b64,
        plaintext_filename: file.name,
        plaintext_content_type: 'application/pdf',
        plaintext_size: file.size,
        encrypted_at: new Date().toISOString(),
      },
      envelope,
    };
    const encryptedBlob = new Blob([JSON.stringify(encryptedManifest)], {
      type: uploadInfo.content_type || 'application/json',
    });

    const formData = new FormData();

    // Add all fields from presigned post
    Object.entries(uploadInfo.fields).forEach(([key, value]) => {
      formData.append(key, value);
    });

    // Add encrypted envelope manifest as upload body
    formData.append('file', encryptedBlob, `${uploadInfo.file_id}.enc.json`);

    const response = await fetch(uploadInfo.upload_url, {
      method: 'POST',
      body: formData,
    });

    if (!response.ok) {
      throw new Error(`Upload failed: ${response.statusText}`);
    }
  };

  const formatFileSize = (bytes) => {
    if (bytes < 1024) return bytes + ' B';
    if (bytes < 1024 * 1024) return (bytes / 1024).toFixed(1) + ' KB';
    return (bytes / (1024 * 1024)).toFixed(1) + ' MB';
  };

  return (
    <div>
      <div className="card">
        <div className="card-header">
          <h2 className="card-title">Upload Documents</h2>
        </div>

        {selectedClient ? (
          <div className="mb-4">
            <span className="text-muted">Uploading to: </span>
            <strong>{selectedClient.client_name}</strong>
            {selectedClient.case_number && (
              <span className="text-muted"> ({selectedClient.case_number})</span>
            )}
          </div>
        ) : (
          <div className="badge badge-warning mb-4">
            No client selected. Please select a client from the Clients page.
          </div>
        )}

        {error && (
          <div className="badge badge-error mb-4">{error}</div>
        )}

        <div
          className={`upload-zone ${dragOver ? 'dragover' : ''}`}
          onDragOver={handleDragOver}
          onDragLeave={handleDragLeave}
          onDrop={handleDrop}
          onClick={() => fileInputRef.current?.click()}
        >
          <div className="upload-icon">📄</div>
          <p className="upload-text">
            <strong>Click to upload</strong> or drag and drop
          </p>
          <p className="text-sm text-muted mt-1">PDF files only (max 50MB each)</p>
          <input
            ref={fileInputRef}
            type="file"
            accept=".pdf"
            multiple
            onChange={handleFileSelect}
            style={{ display: 'none' }}
          />
        </div>

        {files.length > 0 && (
          <div className="mt-4">
            <h3 className="card-title mb-4">Selected Files ({files.length})</h3>
            <ul className="file-list">
              {files.map((file, index) => (
                <li key={index} className="file-item">
                  <div className="file-icon">📄</div>
                  <div className="file-info">
                    <div className="file-name">{file.name}</div>
                    <div className="file-meta">{formatFileSize(file.size)}</div>
                  </div>
                  {uploadProgress[file.name] ? (
                    <div>
                      {uploadProgress[file.name].status === 'uploading' && (
                        <span className="badge badge-pending">Uploading...</span>
                      )}
                      {uploadProgress[file.name].status === 'complete' && (
                        <span className="badge badge-success">Uploaded</span>
                      )}
                      {uploadProgress[file.name].status === 'error' && (
                        <span className="badge badge-error">Failed</span>
                      )}
                    </div>
                  ) : (
                    <button
                      className="btn btn-secondary"
                      onClick={(e) => {
                        e.stopPropagation();
                        removeFile(index);
                      }}
                      disabled={uploading}
                    >
                      Remove
                    </button>
                  )}
                </li>
              ))}
            </ul>

            <div className="mt-4 flex justify-between items-center">
              <button
                className="btn btn-secondary"
                onClick={() => setFiles([])}
                disabled={uploading}
              >
                Clear All
              </button>
              <button
                className="btn btn-primary"
                onClick={uploadFiles}
                disabled={uploading || !selectedClient}
              >
                {uploading ? 'Uploading...' : `Upload ${files.length} Files`}
              </button>
            </div>
          </div>
        )}
      </div>
      <UnlockClientModal
        open={showUnlockModal}
        clientName={unlockClientRecord?.client_name || selectedClient?.client_name}
        loading={unlocking}
        error={unlockError}
        onCancel={() => {
          setShowUnlockModal(false);
          setUnlockClientRecord(null);
        }}
        onUnlock={handleUnlock}
      />
    </div>
  );
}

export default FileUpload;
